const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const STAR_SELECTION_EVENT = 'star:pdp-selection-change';
const controllers = new WeakMap();

class StarPdpSelectionController {
  constructor(root) {
    this.root = root;
    this.picker = root.querySelector('variant-picker[data-star-size-required="true"]');
    this.generation = 0;
    this.selected = this.picker?.dataset.starSizeInitialReady === 'true';
    this.ready = this.selected;
    this.pending = false;
    this.variantId = this.selected ? this.currentVariantId : '';
    this.available = this.selected ? this.currentForm?.dataset.starNativeCanAdd === 'true' : false;
    this.sizeChoiceId = this.selected ? this.currentSizeChoice?.dataset.optionValueId || '' : '';

    this.onChange = this.onChange.bind(this);
    this.onProductSelect = this.onProductSelect.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);

    this.root.addEventListener('change', this.onChange, true);
    this.root.addEventListener(PRODUCT_SELECT_EVENT, this.onProductSelect);
    this.root.addEventListener('submit', this.onSubmit, true);
    this.root.addEventListener('click', this.onClick, true);

    this.preparePicker();
    this.updateState();
    this.syncVariantUrl();
  }

  get currentForm() {
    return this.root.querySelector('product-form-component[data-star-size-gated="true"]');
  }

  get currentVariantId() {
    const value = this.currentForm?.querySelector('input[name="id"]')?.value;
    return value ? String(value) : '';
  }

  get currentSizeChoice() {
    const option = this.picker?.querySelector('[data-star-size-option]');
    if (!option) return null;

    const select = option.matches('select') ? option : option.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const selectedOption = select.selectedOptions[0];
      return selectedOption?.hasAttribute('data-star-size-placeholder') ? null : selectedOption;
    }

    return option.querySelector('input[data-star-size-choice]:checked');
  }

  get label() {
    return this.picker?.dataset.starSelectSizeLabel || this.currentForm?.dataset.starSelectSizeLabel || 'Select a size';
  }

  get errorMessage() {
    return (
      this.picker?.dataset.starSelectSizeError ||
      this.currentForm?.dataset.starSelectSizeError ||
      'Choose a size before adding this product to cart.'
    );
  }

  preparePicker() {
    this.picker = this.root.querySelector('variant-picker[data-star-size-required="true"]');
    if (!this.picker) return;

    if (!this.selected) {
      for (const input of this.picker.querySelectorAll('[data-star-size-option] input[data-star-size-choice]')) {
        input.checked = false;
      }

      const select = this.picker.querySelector('select[data-star-size-select]');
      const placeholder = select?.querySelector('[data-star-size-placeholder]');
      if (select instanceof HTMLSelectElement && placeholder instanceof HTMLOptionElement) {
        for (const option of select.options) option.removeAttribute('selected');
        placeholder.selected = true;
      }
    }

    const sizeOption = this.picker.querySelector('[data-star-size-option]');
    sizeOption?.toggleAttribute('data-star-size-unselected', !this.selected);
    for (const pill of this.picker.querySelectorAll('[data-star-size-option] .variant-option__button-label__pill')) {
      pill.hidden = !this.selected;
    }

    this.updatePrompt();
  }

  updatePrompt() {
    const choice = this.selected ? this.currentSizeChoice : null;
    const value = choice?.value || '';
    const uk = choice?.dataset.starSizeUk || '';
    for (const prompt of this.root.querySelectorAll('[data-star-size-selection-prompt]')) {
      if (value && uk) {
        // Shoe sizes held in EU: show both scales, the switch (snippet star-size-switch) decides which one is visible.
        const ukText = document.createElement('span');
        ukText.className = 'star-size star-size--uk star-size--inline';
        ukText.textContent = `UK ${uk} (EU ${value})`;
        const euText = document.createElement('span');
        euText.className = 'star-size star-size--eu star-size--inline';
        euText.textContent = `EU ${value} (UK ${uk})`;
        prompt.replaceChildren(ukText, euText);
      } else {
        prompt.textContent = value || this.label;
      }
    }
  }

  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.closest('[data-star-size-option]')) return;

    const choice = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    if (!choice || choice.hasAttribute('data-star-size-placeholder')) return;

    this.selected = true;
    this.ready = false;
    this.pending = true;
    this.variantId = '';
    this.available = false;
    this.sizeChoiceId = choice.dataset.optionValueId || '';
    this.hideError();
    this.preparePicker();
    this.updateState();
  }

  onProductSelect(event) {
    if (event.target !== this.picker || !event.promise) return;

    const generation = ++this.generation;
    this.pending = true;
    this.ready = false;
    this.available = false;
    this.updateState();
    if (!this.selected) requestAnimationFrame(() => this.syncVariantUrl());

    Promise.resolve(event.promise)
      .then((result) => {
        if (generation !== this.generation || !this.root.isConnected) return;

        const resource = result?.detail?.resource;
        const responseForm = this.findResponseForm(result?.detail?.html);
        const currentChoice = this.currentSizeChoice;
        const currentChoiceId = currentChoice?.dataset.optionValueId || '';

        this.selected = Boolean(this.selected && currentChoice && currentChoiceId === this.sizeChoiceId);
        this.ready = Boolean(this.selected && resource?.id);
        this.pending = false;
        this.variantId = this.ready ? String(resource.id) : '';
        this.available = this.ready && responseForm?.dataset.starNativeCanAdd === 'true';

        this.updatePrompt();
        this.syncVariantUrl();
        this.deferStateUpdate(responseForm);
      })
      .catch((error) => {
        if (generation !== this.generation || !this.root.isConnected || error?.name === 'AbortError') return;
        this.ready = false;
        this.pending = false;
        this.variantId = '';
        this.available = false;
        this.syncVariantUrl();
        this.deferStateUpdate();
      });
  }

  findResponseForm(html) {
    if (!(html instanceof Document || html instanceof Element)) return null;
    const productId = this.picker?.dataset.productId || '';

    return (
      Array.from(html.querySelectorAll('product-form-component[data-star-size-gated="true"]')).find(
        (form) => form.dataset.productId === productId
      ) || null
    );
  }

  deferStateUpdate(responseForm) {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.preparePicker();
        this.updateState(responseForm);
      });
    });
  }

  updateState(responseForm = null) {
    const forms = this.root.querySelectorAll('product-form-component[data-star-size-gated="true"]');

    this.root.dataset.starSelectionRequired = 'true';
    this.root.dataset.starSelectionReady = String(this.ready);
    this.root.dataset.starSelectionPending = String(this.pending);
    this.root.dataset.starSelectedVariantId = this.variantId;

    for (const form of forms) {
      this.updateForm(form, responseForm);
    }

    this.root.dispatchEvent(
      new CustomEvent(STAR_SELECTION_EVENT, {
        bubbles: true,
        detail: {
          required: true,
          ready: this.ready,
          pending: this.pending,
          variantId: this.variantId,
          available: this.available,
        },
      })
    );
  }

  updateForm(form, responseForm) {
    const button = form.querySelector('[ref="addToCartButton"]');
    const variantInput = form.querySelector('input[name="id"]');
    const acceleratedCheckout = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    const shouldGuard = !this.ready || this.pending;

    if (responseForm) {
      form.dataset.starNativeCanAdd = responseForm.dataset.starNativeCanAdd || 'false';
      form.dataset.starNativeAddLabel = responseForm.dataset.starNativeAddLabel || '';
    }

    if (shouldGuard && variantInput instanceof HTMLInputElement) variantInput.value = '';

    if (button instanceof HTMLButtonElement) {
      if (shouldGuard) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.starSelectionGuard = 'true';
        this.setButtonLabel(button, this.label);
      } else {
        const nativeCanAdd = responseForm?.dataset.starNativeCanAdd ?? form.dataset.starNativeCanAdd;
        const nativeLabel = responseForm?.dataset.starNativeAddLabel || form.dataset.starNativeAddLabel;
        button.disabled = nativeCanAdd !== 'true' || this.available !== true;
        button.setAttribute('aria-disabled', String(button.disabled));
        delete button.dataset.starSelectionGuard;
        if (nativeLabel) this.setButtonLabel(button, nativeLabel);
      }
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      if (shouldGuard || this.available === false) {
        acceleratedCheckout.hidden = true;
        acceleratedCheckout.inert = true;
        acceleratedCheckout.setAttribute('aria-hidden', 'true');
      } else {
        acceleratedCheckout.hidden = false;
        acceleratedCheckout.inert = false;
        acceleratedCheckout.removeAttribute('aria-hidden');
      }
    }
  }

  setButtonLabel(button, label) {
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
    );

    if (textNode) {
      textNode.textContent = ` ${label} `;
    } else {
      button.insertBefore(document.createTextNode(` ${label} `), button.querySelector('.add-to-cart__added'));
    }
  }

  onSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!event.target.closest('product-form-component[data-star-size-gated="true"]')) return;
    if (this.ready && !this.pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showError();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[ref="acceleratedCheckoutButtonContainer"]')) return;
    if (this.ready && !this.pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showError();
  }

  showError() {
    const error = this.root.querySelector('[data-star-size-selection-error]');
    if (error instanceof HTMLElement) {
      error.textContent = this.errorMessage;
      error.hidden = false;
      error.classList.remove('hidden');
    }

    const option = this.picker?.querySelector('[data-star-size-option]');
    option?.setAttribute('aria-invalid', 'true');

    const focusTarget =
      option?.querySelector('select') ||
      option?.querySelector('input[data-star-size-choice]:not([aria-disabled="true"])') ||
      option?.querySelector('input[data-star-size-choice]');

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
      focusTarget.scrollIntoView({
        block: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }
  }

  hideError() {
    const error = this.root.querySelector('[data-star-size-selection-error]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.classList.add('hidden');
    }
    this.picker?.querySelector('[data-star-size-option]')?.removeAttribute('aria-invalid');
  }

  syncVariantUrl() {
    const url = new URL(window.location.href);
    if (this.ready && this.variantId) {
      url.searchParams.set('variant', this.variantId);
    } else if (!this.selected) {
      url.searchParams.delete('variant');
    }

    if (url.href !== window.location.href) {
      history.replaceState(history.state, '', url);
    }
  }

  onPageShow() {
    this.preparePicker();
    this.updateState();
  }
}

function initialize(root = document) {
  const roots = root instanceof Element && root.matches('[data-star-pdp]')
    ? [root]
    : root.querySelectorAll('[data-star-pdp]');

  for (const pdp of roots) {
    if (controllers.has(pdp)) continue;
    if (!pdp.querySelector('variant-picker[data-star-size-required="true"]')) {
      pdp.dataset.starSelectionRequired = 'false';
      pdp.dataset.starSelectionReady = 'true';
      pdp.dataset.starSelectionPending = 'false';
      continue;
    }

    controllers.set(pdp, new StarPdpSelectionController(pdp));
  }
}

initialize();

window.addEventListener('pageshow', () => {
  for (const pdp of document.querySelectorAll('[data-star-pdp]')) {
    controllers.get(pdp)?.onPageShow();
  }
});

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      const parentPdp = node.closest('[data-star-pdp]');
      if (parentPdp) initialize(parentPdp);
      initialize(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
